import { Badge, Button, Collapse, Descriptions, Popconfirm, Table, Tag } from "antd";
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { MergerSectionProps } from "../helpers/shared-interfaces";
import { tabPaneStyle } from "../helpers/constants";

export const MergerSection: React.FC<MergerSectionProps> = ({
  mergeLogs,
  mergedTo,
  onReverse,
  onReverseAll,
  onInitiate,
  isReversing,
  isBulkReversing,
  type,
  canManage,
}) => {
  const [reversingId, setReversingId] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleReverse = (id: string) => {
    setReversingId(id);
    onReverse(id);
  };

  const columns = [
    {
      title: "Duplicate Account",
      key: "duplicate",
      render: (log: any) => {
        const duplicate = type === "customer" ? log.duplicateCustomer : log.duplicateCompany;
        const linkPath = type === "customer" ? `/customers/${duplicate.id}` : `/companies/${duplicate.id}`;
        return (
          <div>
            <NavLink to={linkPath} style={{ fontWeight: "bold" }}>
              {duplicate.fullname || duplicate.name}
            </NavLink>
            <Tag color="blue" style={{ marginLeft: "8px" }}>{duplicate.petraId}</Tag>
          </div>
        );
      },
    },
    {
      title: "Date Merged",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => date ? new Date(date).toLocaleDateString() : "N/A",
    },
    {
      title: "Action",
      key: "action",
      render: (log: any) => {
        const duplicate = type === "customer" ? log.duplicateCustomer : log.duplicateCompany;
        return (
          <Popconfirm
            title="Are you sure you want to reverse this merge?"
            onConfirm={() => handleReverse(duplicate.id)}
            okText="Yes"
            cancelText="No"
            disabled={!canManage}
          >
            <Button
              danger
              disabled={!canManage}
              loading={isReversing && reversingId === duplicate.id}
              style={{ border: "1px solid #ff4d4f", borderRadius: "4px" }}
            >
              Reverse Merge
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  if (mergeLogs.length === 0 && !canManage) return null;

  return (
    <div className="section-card" style={{ marginTop: "20px", marginBottom: "20px" }}>
      <Descriptions
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <div>Account Mergers</div>
            <div style={{ display: "flex", gap: "8px" }}>
              {/* {canManage && mergedTo && mergeLogs.length > 0 && onReverseAll && ( */}
              {onReverseAll && mergeLogs.length > 0 && (
                <Popconfirm
                  title={selectedRowKeys.length > 0
                    ? `Are you sure you want to reverse the ${selectedRowKeys.length} selected merges?`
                    : "Are you sure you want to reverse ALL merges for this account?"
                  }
                  onConfirm={() => {
                    if (selectedRowKeys.length > 0) {
                      const logs: any[] = mergeLogs as any[];
                      const selectedDuplicateIds = logs
                        .filter((log) => selectedRowKeys.includes(log.id))
                        .map((log) => type === "customer" ? log.duplicateCustomer.id : log.duplicateCompany.id);
                      onReverseAll(selectedDuplicateIds);
                      setSelectedRowKeys([]);
                    } else {
                      onReverseAll();
                    }
                  }}
                  okText="Yes, Reverse"
                  cancelText="No"
                  disabled={isBulkReversing}
                >
                  <Button
                    danger
                    loading={isBulkReversing}
                    style={{ borderRadius: "4px" }}
                  >
                    {selectedRowKeys.length > 0
                      ? `Reverse Selected Merges (${selectedRowKeys.length})`
                      : mergeLogs.length > 1 ? "Reverse All Merges" : "Reverse Merge"
                    }
                  </Button>
                </Popconfirm>
              )}
              {canManage && !mergedTo && (
                <Button className="base-button" onClick={onInitiate}>
                  Initiate New Merger
                </Button>
              )}
            </div>
          </div>
        }
      ></Descriptions>

      {mergedTo && (
        <Descriptions bordered size="small" style={{ marginBottom: "16px" }}>
          <Descriptions.Item label={<b>Merged To</b>}>
            <NavLink
              to={type === "customer" ? `/customers/${mergedTo.id}` : `/companies/${mergedTo.id}`}
              style={{ fontWeight: "bold" }}
            >
              {mergedTo.fullname || mergedTo.name}
            </NavLink>
            <Tag color="blue" style={{ marginLeft: "8px" }}>{mergedTo.petraId}</Tag>
          </Descriptions.Item>
        </Descriptions>
      )}

      <Collapse
        style={{ marginBottom: "12px" }}
        accordion={true}
      >
        <Collapse.Panel
          key="accountMergers"
          header={
            <span>
              Merged From Accounts{" "}
              <Badge count={mergeLogs.length} style={tabPaneStyle} />
            </span>
          }
        >
          <Table
            dataSource={mergeLogs as any[]}
            columns={columns}
            rowKey="id"
            rowSelection={rowSelection}
            pagination={false}
            locale={{ emptyText: "No merged accounts found for this record." }}
          />
        </Collapse.Panel>
      </Collapse>
    </div>
  );
};
